// loginForm — Phase 6 Task 7 LoginPage の各経路 validation + outcome マップ.
//
// LoginPage は cross-room session を取得するためだけに存在する画面 (admin_token
// 経路は localStorage 自動利用、Task 6.6-B `RoomSettingsModal` で消費済み).
// ここでは:
//   1. オーナーパスワード経路: roomId (UUID) + password (8..128) を validate.
//   2. magic link 経路: email を validate.
//   3. DispatcherClient の outcome を UI 表示用 `{ kind, message }` に変換.
//
// Why a separate module:
// 既存の `createRoomForm.ts` / `roomSettingsForm.ts` と同じ流儀で、DOM 引かない
// pure function として vitest node-only で網羅. LoginPage.tsx は薄い wiring に留める.
//
// 確定方針 (consult 2026-05-04):
// - AB. オーナーパスワードフォーム = roomId + password をユーザがフォーム入力
//   (URL クエリ自動入れではない). roomId は UUID v4 形式.
// - magic link 経路の purpose = 'recovery' (cross-room) 固定. UI は roomId を
//   要求しない. 'room_admin' は使わない (specific room の再認証は単発オーナー
//   経路 = `RoomSettingsModal` の email field 経由で対応済).

import type {
  LoginByOwnerPasswordOutcome,
  SendMagicLinkOutcome,
} from '../infrastructure/dispatcher/DispatcherClient.js';
import { formatMs } from './uiFeedback.js';

/* -----------------------------------------------------------------------------
 * Constants
 * --------------------------------------------------------------------------- */

/** Phase 6 Task 6 と同じ仕様 (UI 側 8..128). */
export const OWNER_PASSWORD_MIN_LENGTH = 8;
export const OWNER_PASSWORD_MAX_LENGTH = 128;

/** Server `EmailSchema.max(320)` と一致. */
export const EMAIL_MAX_LENGTH = 320;

/* -----------------------------------------------------------------------------
 * Validation: owner password 経路 (roomId + password)
 * --------------------------------------------------------------------------- */

export type RoomIdError = 'empty' | 'invalid-format';
export type LoginPasswordError = 'too-short' | 'too-long';

export interface OwnerPasswordLoginValues {
  /** UUID v4 文字列 (trim 後). */
  readonly roomId: string;
  /** plain text. trim しない (passphrase の前後空白も意味あり得る). */
  readonly password: string;
}

export type OwnerPasswordLoginValidation =
  | {
      readonly outcome: 'ok';
      /** Trimmed roomId + raw password — DispatcherClient の input にそのまま渡せる. */
      readonly value: OwnerPasswordLoginValues;
    }
  | {
      readonly outcome: 'failed';
      readonly errors: {
        readonly roomId?: RoomIdError;
        readonly password?: LoginPasswordError;
      };
    };

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const validateOwnerPasswordLogin = (
  values: OwnerPasswordLoginValues,
): OwnerPasswordLoginValidation => {
  const errors: { roomId?: RoomIdError; password?: LoginPasswordError } = {};
  const trimmedRoomId = values.roomId.trim();
  if (trimmedRoomId.length === 0) {
    errors.roomId = 'empty';
  } else if (!UUID_RE.test(trimmedRoomId)) {
    errors.roomId = 'invalid-format';
  }

  if (values.password.length < OWNER_PASSWORD_MIN_LENGTH) {
    errors.password = 'too-short';
  } else if (values.password.length > OWNER_PASSWORD_MAX_LENGTH) {
    errors.password = 'too-long';
  }

  if (Object.keys(errors).length > 0) {
    return { outcome: 'failed', errors };
  }
  return {
    outcome: 'ok',
    value: { roomId: trimmedRoomId, password: values.password },
  };
};

/* -----------------------------------------------------------------------------
 * Validation: magic link 経路 (email)
 * --------------------------------------------------------------------------- */

export type EmailError = 'empty' | 'too-long' | 'invalid-format';

export type EmailValidation =
  | { readonly outcome: 'ok'; readonly value: string }
  | { readonly outcome: 'failed'; readonly error: EmailError };

/**
 * 軽量 email regex (server zod `.email()` が源泉、UI は早期 reject 用).
 * createRoomForm / roomSettingsForm と同パターン.
 */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const validateEmailForLogin = (raw: string): EmailValidation => {
  const trimmed = raw.trim();
  if (trimmed.length === 0) {
    return { outcome: 'failed', error: 'empty' };
  }
  if (trimmed.length > EMAIL_MAX_LENGTH) {
    return { outcome: 'failed', error: 'too-long' };
  }
  if (!EMAIL_RE.test(trimmed)) {
    return { outcome: 'failed', error: 'invalid-format' };
  }
  return { outcome: 'ok', value: trimmed };
};

/* -----------------------------------------------------------------------------
 * Field error → user-facing string
 * --------------------------------------------------------------------------- */

export const roomIdErrorText = (err: RoomIdError): string => {
  switch (err) {
    case 'empty':
      return 'ルーム ID を入力してください。';
    case 'invalid-format':
      return 'ルーム ID の形式が正しくありません (UUID 形式)。';
  }
};

export const loginPasswordErrorText = (err: LoginPasswordError): string => {
  switch (err) {
    case 'too-short':
      return `オーナーパスワードは ${OWNER_PASSWORD_MIN_LENGTH} 文字以上で入力してください。`;
    case 'too-long':
      return `オーナーパスワードは ${OWNER_PASSWORD_MAX_LENGTH} 文字以内で入力してください。`;
  }
};

export const emailErrorText = (err: EmailError): string => {
  switch (err) {
    case 'empty':
      return 'メールアドレスを入力してください。';
    case 'too-long':
      return `メールアドレスは ${EMAIL_MAX_LENGTH} 文字以内で入力してください。`;
    case 'invalid-format':
      return 'メールアドレスの形式が正しくありません。';
  }
};

/* -----------------------------------------------------------------------------
 * Outcome → screen-side state
 *
 * UI 表示用 `{ kind: 'success' | 'error' | 'info', message }` に潰す.
 *
 *  - 'success' : ログイン or 送信成功 (次画面遷移)
 *  - 'info'    : 情報表示 (rate-limited 等、再試行待ちで blocking はせず inline 表示)
 *  - 'error'   : ブロッキングエラー (フォームは再入力可)
 *
 * 'unauthorized' は enumeration 防止のため「room 不在 / pw 不一致 / hash 不在」
 * を全部同じ文言に倒す.
 * --------------------------------------------------------------------------- */

export interface LoginUiOutcome {
  readonly kind: 'success' | 'error' | 'info';
  readonly message: string;
}

const UNAUTHORIZED_LOGIN_MESSAGE = 'ルーム ID またはオーナーパスワードが正しくありません。';
const INVALID_BODY_LOGIN_MESSAGE = '入力内容が正しくありません。';
const NETWORK_LOGIN_MESSAGE = 'エラーが発生しました。時間を置いて再度お試しください。';

/**
 * Map `loginByOwnerPassword` outcome → UI 表示用 outcome.
 *
 * 'ok' は **success** (= 上位 LoginPage が adminToken を保存して pathname `/<id>`
 * へ navigate). 失敗は全部 'error' or 'info' に倒す.
 */
export const mapLoginByOwnerPasswordOutcome = (
  out: LoginByOwnerPasswordOutcome,
): LoginUiOutcome => {
  switch (out.outcome) {
    case 'ok':
      return { kind: 'success', message: 'ログインに成功しました。' };
    case 'unauthorized':
      return { kind: 'error', message: UNAUTHORIZED_LOGIN_MESSAGE };
    case 'invalid-body':
      return { kind: 'error', message: INVALID_BODY_LOGIN_MESSAGE };
    case 'rate-limited':
      return {
        kind: 'info',
        message: `操作制限に達しました (${formatMs(out.retryAfterMs)} 後に再試行してください)。`,
      };
    case 'unknown':
      return { kind: 'error', message: NETWORK_LOGIN_MESSAGE };
  }
};

/**
 * Map `sendMagicLink` outcome → UI 表示用 outcome.
 *
 * 'ok' は backend が常に返す (enumeration 防止) ので、UI は「該当アドレスにメール
 * を送信しました (届かない場合は登録されていない可能性があります)」相当の固定
 * splash を出す. UI コンポーネントが文言を持つので、ここでは抽象的な "success".
 */
export const mapSendMagicLinkOutcome = (out: SendMagicLinkOutcome): LoginUiOutcome => {
  switch (out.outcome) {
    case 'ok':
      return {
        kind: 'success',
        message:
          '入力されたアドレス宛にメールを送信しました (該当アカウントが存在する場合)。受信トレイをご確認ください。',
      };
    case 'invalid-body':
      return { kind: 'error', message: INVALID_BODY_LOGIN_MESSAGE };
    case 'rate-limited':
      return {
        kind: 'info',
        message: `送信回数制限に達しました (${formatMs(out.retryAfterMs)} 後に再試行してください)。`,
      };
    case 'unknown':
      return { kind: 'error', message: NETWORK_LOGIN_MESSAGE };
  }
};
